// File: src/pages/LockPartyReport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Input,
  Row,
  Modal,
  Col,
  ModalHeader,
  ModalBody,
  Badge,
  Label,
  Button,
  Spinner,
  Table,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { FaFilter } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import {
  BsFillArrowDownSquareFill,
  BsFillArrowUpSquareFill,
} from "react-icons/bs";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import swal from "sweetalert";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

// ✅ your app-provided imports (kept under ../import)
import {
  _Get,
  _Post,
  CreateCustomerList,
} from "../import/ApiEndPoint/ApiCalling";
import { View_Ledger_by_id } from "../import/ApiEndPoint/Api";
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";

const getUser = () => JSON.parse(localStorage.getItem("userData") || "{}");
const fmt = (n) => (typeof n === "number" ? n.toFixed(2) : n);

// ===== inline styles only (no scss) =====
const injectOnce = () => {
  if (document.getElementById("lock-party-inline-css")) return;
  const s = document.createElement("style");
  s.id = "lock-party-inline-css";
  s.textContent = `
    .lp-card{ border:1px solid #e5e7eb; border-radius:14px; }
    .lp-toolbar .pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .lp-dropdown{ position:relative; display:inline-block; }
    .lp-dropdown-menu{ position:absolute; right:0; top:26px; z-index:10; background:#fff; border:1px solid #39cccc; border-radius:8px; min-width:120px; padding:8px; }
    .lp-table th{ background:#f4f7f6; font-size:12px; text-transform:uppercase; letter-spacing:.03em; }
    .lp-table td, .lp-table th{ vertical-align:middle; }
    .lp-stats{ border:1px solid #e5e7eb; background:#f8fafc; border-radius:12px; padding:14px; width:330px; }
    .lp-stats .row{ font-weight:700; font-size:16px; }
    .lp-click{ cursor:pointer; }
    .lp-chip{ display:inline-grid; place-items:center; width:26px; height:26px; border-radius:14px; background:#e2e8f0; }
    .lp-modal-pick{ max-height:340px; overflow:auto; border:1px dashed #e5e7eb; border-radius:10px; padding:6px; }
    .lp-active{ background:#1877f2; color:#fff; }
  `;
  document.head.appendChild(s);
};

// ===== columns config =====
const ALL_COLS = [
  { id: "uid", label: "UID" },
  { id: "CompanyName", label: "Party name" },
  { id: "limit", label: "Limit" },
  { id: "lockInTime", label: "Credit Period" },
  { id: "dueDays", label: "Due Days" },
  { id: "overDueDays", label: "Over Due days" },
  { id: "lockStatus", label: "Lock Status" },
  { id: "debit", label: "Debit" },
  { id: "credit", label: "Credit" },
  { id: "closing", label: "Closing" },
];

export default function LockPartyReport() {
  useEffect(() => injectOnce(), []);
  const navigate = useNavigate();

  // ===== permissions / master =====
  const [insiderPermissions, setInsiderPermissions] = useState({});
  const [masterShow, setMasterShow] = useState(false);

  // ===== state =====
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]); // current filtered rows (search/date)
  const [allRows, setAllRows] = useState([]); // raw rows
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // column picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selIdx, setSelIdx] = useState(-1);
  const [visibleCols, setVisibleCols] = useState(() => {
    const saved = localStorage.getItem("DueReportListSimple");
    if (saved) {
      try {
        const ids = JSON.parse(saved);
        return ALL_COLS.filter((c) => ids.includes(c.id));
      } catch {}
    }
    // default: all
    return [...ALL_COLS];
  });
  const [availableCols, setAvailableCols] = useState(() =>
    ALL_COLS.filter((c) => !visibleCols.some((v) => v.id === c.id))
  );
  const hoverRef = useRef(null);

  // ===== calc helpers =====
  const calcDueAndOverdue = (ledgerRes, lockInTime = 0, closing) => {
    const today = new Date();

    if (closing < 0) return { dueDays: 0, overDueDays: 0 };

    const debits = [];
    const credits = [];

    const led = Array.isArray(ledgerRes?.Ledger) ? ledgerRes.Ledger : [];
    for (const e of led) {
      if (!e?.date) continue;
      const dt = new Date(e.date);
      if (startDate && new Date(dt) < new Date(startDate)) continue;
      if (endDate && new Date(dt) > new Date(endDate)) continue;

      if (e.debit) debits.push({ amount: Number(e.debit), date: dt });
      if (e.credit) credits.push({ amount: Number(e.credit), date: dt });
    }

    debits.sort((a, b) => a.date - b.date);
    credits.sort((a, b) => a.date - b.date);

    let unpaid = [...debits];
    for (const c of credits) {
      let camt = c.amount;
      while (camt > 0 && unpaid.length) {
        if (camt >= unpaid[0].amount) {
          camt -= unpaid[0].amount;
          unpaid.shift();
        } else {
          unpaid[0].amount -= camt;
          camt = 0;
        }
      }
    }

    if (unpaid.length) {
      const last = unpaid[0];
      const dueDate = new Date(last.date);
      dueDate.setDate(dueDate.getDate() + Number(lockInTime || 0));
      const diff = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
      return {
        dueDays: diff > 0 ? diff : 0,
        overDueDays: diff < 0 ? Math.abs(diff) : 0,
      };
    }
    return { dueDays: 0, overDueDays: 0 };
  };

  const buildRows = async (ownerId, db) => {
    setLoading(true);
    try {
      const customerRes = await CreateCustomerList(ownerId, db);
      const customers = Array.isArray(customerRes?.Customer)
        ? customerRes.Customer
        : [];

      const out = [];
      for (const cust of customers) {
        const PartyId = cust?._id;
        if (!PartyId) continue;

        try {
          const ledgerRes = await _Get(View_Ledger_by_id, PartyId);

          // total debit/credit including opening balance (Type)
          let totalDebit = 0;
          let totalCredit = 0;

          const led = Array.isArray(ledgerRes?.Ledger) ? ledgerRes.Ledger : [];
          for (const e of led) {
            const dtOK =
              (!startDate || new Date(e?.date) >= new Date(startDate)) &&
              (!endDate || new Date(e?.date) <= new Date(endDate));
            if (!dtOK) continue;
            if (e?.debit) totalDebit += Number(e.debit);
            if (e?.credit) totalCredit += Number(e.credit);
          }

          const opening = Number(cust?.OpeningBalance || 0);
          const openingType = String(cust?.Type || "").toLowerCase();
          if (openingType === "debit") totalDebit += opening;
          if (openingType === "credit") totalCredit += opening;

          // closing = debit - credit (negative -> Cr)
          const closing = totalDebit - totalCredit;
          const { dueDays, overDueDays } = calcDueAndOverdue(
            ledgerRes,
            cust?.lockInTime,
            closing
          );

          out.push({
            partyId: PartyId,
            CompanyName: cust?.CompanyName || "",
            limit: Number(cust?.limit || 0),
            lockInTime: Number(cust?.lockInTime || 0),
            dueDays,
            overDueDays,
            debit: Number(totalDebit),
            credit: Number(totalCredit),
            closing: Number(closing),
          });
        } catch (e) {
          // ignore one customer failure, continue
          // console.log("ledger error", PartyId, e);
        }
      }

      // keep only locked (like your previous filter) OR keep all? — we keep all and show lock status
      setAllRows(out);
      setRows(out);
    } catch (err) {
      console.error(err);
      setAllRows([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ===== initial mount =====
  useEffect(() => {
    const u = getUser();
    setMasterShow(u?.rolename?.roleName === "MASTER");
    setInsiderPermissions(CheckPermission("Due Report") || {});
    buildRows(u?._id, u?.database);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== search filter =====
  useEffect(() => {
    const t = search.trim().toLowerCase();
    if (!t) return setRows(allRows);
    setRows(
      allRows.filter((r) =>
        String(r.CompanyName || "")
          .toLowerCase()
          .includes(t)
      )
    );
  }, [search, allRows]);

  // ===== totals =====
  const totals = useMemo(() => {
    const td = rows.reduce((s, r) => s + (Number(r.debit) || 0), 0);
    const tc = rows.reduce((s, r) => s + (Number(r.credit) || 0), 0);
    const closing = td - tc; // match row logic
    const type = closing < 0 ? "Cr" : "Dr";
    return { td, tc, closing: Math.abs(closing), type };
  }, [rows]);

  // ===== column picker logic =====
  const saveVisibleCols = (arr) => {
    setVisibleCols(arr);
    localStorage.setItem(
      "DueReportListSimple",
      JSON.stringify(arr.map((c) => c.id))
    );
  };
  const addCols = (ids) => {
    const adds = availableCols.filter((c) => ids.includes(c.id));
    const next = [...visibleCols, ...adds];
    const nextAvail = availableCols.filter((c) => !ids.includes(c.id));
    setAvailableCols(nextAvail);
    saveVisibleCols(next);
  };
  const removeAt = (idx) => {
    const next = [...visibleCols];
    const [rm] = next.splice(idx, 1);
    if (rm) setAvailableCols((p) => [...p, rm]);
    saveVisibleCols(next);
    setSelIdx(-1);
  };
  const moveUp = () => {
    if (selIdx <= 0) return;
    const next = [...visibleCols];
    const [it] = next.splice(selIdx, 1);
    next.splice(selIdx - 1, 0, it);
    setSelIdx(selIdx - 1);
    saveVisibleCols(next);
  };
  const moveDown = () => {
    if (selIdx < 0 || selIdx >= visibleCols.length - 1) return;
    const next = [...visibleCols];
    const [it] = next.splice(selIdx, 1);
    next.splice(selIdx + 1, 0, it);
    setSelIdx(selIdx + 1);
    saveVisibleCols(next);
  };

  // ===== export helpers (use current rows + visible columns) =====
  const currentTable = useMemo(() => {
    return rows.map((r, i) => {
      const lockStatus = r.overDueDays > 0 ? "Locked" : "Active";
      const closingTxt = `${fmt(Math.abs(r.closing))} ${
        r.closing < 0 ? "Cr" : "Dr"
      }`;
      const map = {
        uid: i + 1,
        CompanyName: r.CompanyName,
        limit: fmt(r.limit),
        lockInTime: r.lockInTime,
        dueDays: r.dueDays,
        overDueDays: r.overDueDays,
        lockStatus,
        debit: fmt(r.debit),
        credit: fmt(r.credit),
        closing: closingTxt,
      };
      const obj = {};
      for (const c of visibleCols) obj[c.label] = map[c.id];
      return obj;
    });
  }, [rows, visibleCols]);

  const exportCSV = () => {
    const csv = Papa.unparse(currentTable);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dueReport.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportXLS = () => {
    // legacy .xls (actually XLSX but with .xls extension, same as original)
    const ws = XLSX.utils.json_to_sheet(currentTable);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "dueReport.xls");
  };

  const exportXLSX = () => {
    const ws = XLSX.utils.json_to_sheet(currentTable);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "dueReport.xlsx");
  };

  const exportXML = () => {
    const headers = Object.keys(currentTable[0] || {});
    let xml = "<root>\n";
    for (const row of currentTable) {
      xml += "  <row>\n";
      for (const h of headers) {
        xml += `    <${h.replace(/\s+/g, "_")}>${String(row[h] ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")}</${h.replace(/\s+/g, "_")}>\n`;
      }
      xml += "  </row>\n";
    }
    xml += "</root>";
    const blob = new Blob([xml], { type: "text/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dueReport.xml";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!currentTable.length) return;
    const doc = new jsPDF("landscape");
    const head = [Object.keys(currentTable[0])];
    const body = currentTable.map((r) => Object.values(r));
    doc.text("Lock Party Report", 14, 14);
    doc.autoTable({ head, body, startY: 20 });
    doc.save("dueReport.pdf");
  };

  // ===== master switch (for MASTER users) =====
  const handleMasterSubmit = (e) => {
    e?.preventDefault?.();
    const raw = localStorage.getItem("SuperadminIdByMaster");
    if (!raw) return;
    const [id, db] = JSON.parse(raw).split(" ");
    if (!id || !db) return;
    buildRows(id, db);
  };

  // ===== date filter submit =====
  const handleDateSubmit = () => {
    const u = getUser();
    buildRows(u?._id, u?.database);
  };

  // ===== render =====
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Card className="lp-card">
        <Row className="lp-toolbar" style={{ margin: 8 }}>
          <Col>
            <h3 style={{ fontWeight: 600, marginTop: 14 }}>
              Lock Party Report
            </h3>
          </Col>

          {masterShow ? (
            <Col lg="3" md="4" sm="12" style={{ marginTop: 10 }}>
              <SuperAdminUI
                onDropdownChange={(v) =>
                  localStorage.setItem(
                    "SuperadminIdByMaster",
                    JSON.stringify(v)
                  )
                }
                onSubmit={handleMasterSubmit}
              />
            </Col>
          ) : (
            <Col />
          )}

          <Col lg="3" md="4" sm="12" style={{ marginTop: 10 }}>
            <Input
              placeholder="Search party..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>

          <Col lg="4" md="6" sm="12" style={{ marginTop: 10 }}>
            <Row>
              <Col md="5">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col md="5">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
              <Col md="2" style={{ display: "flex", alignItems: "flex-end" }}>
                <Button color="primary" onClick={handleDateSubmit}>
                  Submit
                </Button>
              </Col>
            </Row>
          </Col>

          <Col
            lg="2"
            md="3"
            sm="12"
            style={{
              marginTop: 14,
              display: "flex",
              gap: 12,
              justifyContent: "flex-end",
            }}
          >
            {insiderPermissions?.View && (
              <FaFilter
                className="lp-click"
                title="Choose Columns"
                size={20}
                color="#39cccc"
                onClick={() => setPickerOpen(true)}
              />
            )}

            {insiderPermissions?.Download && (
              <div
                className="lp-dropdown"
                onMouseEnter={() => setIsOpen(true)}
                onMouseLeave={() => setIsOpen(false)}
                ref={hoverRef}
              >
                <ImDownload
                  className="lp-click"
                  title="Download"
                  size={20}
                  color="#39cccc"
                />
                {isOpen && (
                  <div className="lp-dropdown-menu">
                    <h6
                      className="lp-click"
                      onClick={exportPDF}
                      style={{ margin: 6 }}
                    >
                      .PDF
                    </h6>
                    <h6
                      className="lp-click"
                      onClick={exportCSV}
                      style={{ margin: 6 }}
                    >
                      .CSV
                    </h6>
                    <h6
                      className="lp-click"
                      onClick={exportXLS}
                      style={{ margin: 6 }}
                    >
                      .XLS
                    </h6>
                    <h6
                      className="lp-click"
                      onClick={exportXLSX}
                      style={{ margin: 6 }}
                    >
                      .XLSX
                    </h6>
                    <h6
                      className="lp-click"
                      onClick={exportXML}
                      style={{ margin: 6 }}
                    >
                      .XML
                    </h6>
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>

        <CardBody style={{ marginTop: -12 }}>
          <div style={{ width: "100%", overflowX: "auto" }}>
            <Table responsive bordered hover className="lp-table">
              <thead>
                <tr>
                  {visibleCols.map((c) => (
                    <th key={c.id}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleCols.length}
                      style={{ textAlign: "center", color: "#64748b" }}
                    >
                      No data found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const lockStatus = r.overDueDays > 0 ? "Locked" : "Active";
                    const closingTxt = `${fmt(Math.abs(r.closing))} ${
                      r.closing < 0 ? "Cr" : "Dr"
                    }`;

                    const map = {
                      uid: idx + 1,
                      CompanyName: r.CompanyName,
                      limit: fmt(r.limit),
                      lockInTime: r.lockInTime,
                      dueDays: r.dueDays,
                      overDueDays: r.overDueDays,
                      lockStatus: (
                        <span
                          style={{ color: r.overDueDays > 0 ? "red" : "green" }}
                        >
                          {lockStatus}
                        </span>
                      ),
                      debit: fmt(r.debit),
                      credit: fmt(r.credit),
                      closing: closingTxt,
                    };

                    return (
                      <tr key={r.partyId || idx}>
                        {visibleCols.map((c) => (
                          <td key={c.id}>{map[c.id]}</td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <div className="lp-stats">
              <div className="row">
                Debit:{" "}
                <span style={{ color: "#d9534f" }}> ₹ {fmt(totals.td)}</span>
              </div>
              <div className="row">
                Credit:{" "}
                <span style={{ color: "#5bc0de" }}> ₹ {fmt(totals.tc)}</span>
              </div>
              <div className="row">
                Closing:{" "}
                <span
                  style={{
                    color: totals.type === "Cr" ? "#5cb85c" : "#d9534f",
                  }}
                >
                  ₹ {fmt(totals.closing)} {totals.type}
                </span>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Column Picker Modal */}
      <Modal
        isOpen={pickerOpen}
        toggle={() => setPickerOpen(false)}
        backdrop="static"
        size="lg"
      >
        <ModalHeader toggle={() => setPickerOpen(false)}>
          Change Fields
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12">
              <h5>Available Columns</h5>
              <div className="lp-modal-pick">
                {availableCols.map((c) => (
                  <div key={c.id} className="d-flex align-items-center mb-1">
                    <input
                      type="checkbox"
                      className="mr-1"
                      onChange={(e) =>
                        e.target.checked ? addCols([c.id]) : null
                      }
                    />
                    <span>{c.label}</span>
                  </div>
                ))}
              </div>
            </Col>

            <Col
              lg="2"
              md="2"
              sm="12"
              className="d-flex align-items-center justify-content-center"
            >
              <div className="d-flex flex-column align-items-center">
                <div className="mb-2">
                  <BsFillArrowUpSquareFill
                    size={30}
                    className="lp-click"
                    onClick={moveUp}
                  />
                </div>
                <div>
                  <BsFillArrowDownSquareFill
                    size={30}
                    className="lp-click"
                    onClick={moveDown}
                  />
                </div>
              </div>
            </Col>

            <Col lg="6" md="6" sm="12">
              <h5>Visible Columns</h5>
              <div className="lp-modal-pick">
                {visibleCols.map((c, i) => (
                  <div
                    key={c.id}
                    className={`d-flex align-items-center mb-1 ${
                      selIdx === i ? "lp-active" : ""
                    }`}
                    style={{ padding: 4, borderRadius: 8 }}
                  >
                    <IoMdRemoveCircleOutline
                      className="mr-1 lp-click"
                      size={22}
                      color="red"
                      onClick={() => removeAt(i)}
                      title="Remove"
                    />
                    <span className="lp-click" onClick={() => setSelIdx(i)}>
                      {c.label}
                    </span>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col className="d-flex justify-content-center">
              <Badge
                color="primary"
                className="lp-click"
                onClick={() => setPickerOpen(false)}
              >
                Submit
              </Badge>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}
